import styles from "./styles.module.css";
import { useDeviceType } from "hooks";
import { theme } from "../../../constants/theme";
import { CustomHeadline, CustomText } from "components/ui";
import { CustomButtonSection } from "components/custom-button-section";
import desktopImageBackground from "assets/images/desktop-preview.png";
import mobileImageBackground from "assets/images/mobile-preview.png";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useState, useEffect } from "react";
import { fetchBlogs } from "services/blogService";
import { Blog } from "types/blog";
import { Navbar } from "components/navbar";
import { Modal } from "components/modal";
import SafeImage from "./SafeImage";

function trimWithWordBoundary(str: string, max: number): string {
  if (str.length <= max) return str;
  const trimmed = str.slice(0, max);
  return trimmed.slice(0, trimmed.lastIndexOf(" ")) + "...";
}

export const Section13: React.FC = () => {
  const [translate, setTranslate] = useState(0);
  const [activeSection, setActiveSection] = useState<number>(0);
  const { device } = useDeviceType();
  const isDesktop = device === "desktop";
  const isMobile = device === "s-mobile";
  const navigate = useNavigate();

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [dynamicDefaultDesktopPreview, setDynamicDefaultDesktopPreview] =
    useState("");
  const [dynamicDefaultMobilePreview, setDynamicDefaultMobilePreview] =
    useState("");

  useEffect(() => {
    fetchBlogs()
      .then((data: Blog[]) => {
        setBlogs(
          data.filter((blog) => {
            return blog.status === "publish";
          })
        );
        const defaultImageData = data?.find((blog) => {
          return blog.status === "default-image";
        });
        setDynamicDefaultDesktopPreview(
          defaultImageData?.desktopPreviewImageUrl || ""
        );
        setDynamicDefaultMobilePreview(
          defaultImageData?.mobilePreviewImageUrl || ""
        );
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const handleArticleClick = (slug: string) => {
    navigate(`/article/${slug}`);
  };

  const navigateToTop = () => navigate("/");

  useEffect(() => {
    if (blogs && blogs.length > 0 && window.location.hash) {
      const id = window.location.hash.substring(1); // remove "#"
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [blogs]);

  return (
    <div className={styles.section13}>
      <Navbar
        activeSection={activeSection}
        navigateToTop={navigateToTop}
        theme="black"
        blur
      />
      <Modal setTranslate={setTranslate} setActiveSection={setActiveSection} />
      <div className={styles.container}>
        <div className={styles.content}>
          <CustomHeadline
            size={isDesktop ? 55 : 24}
            text="Blog"
            color={theme.colors.neutral[20]}
            styles={{
              lineHeight: `${isDesktop ? 52 : 26}px`,
              marginBottom: "30px",
              fontWeight: 700,
            }}
          />

          {loading && (
            <>
              <div className={styles.skeletonLoader} />
              <div className={styles.skeletonLoader} />
            </>
          )}
          {!loading &&
            blogs &&
            blogs.length > 0 &&
            blogs.map((blog, index) => {
              if (!blog) return null;
              return (
                <div
                  key={blog.id}
                  id={`${blog.slug}`}
                  onClick={() => blog.slug && handleArticleClick(blog.slug)}
                  style={{ cursor: "pointer" }}
                >
                  {index !== 0 && (
                    <div
                      style={{
                        width: "100%",
                        padding: isDesktop ? "40px 0px" : "30px 0px",
                      }}
                    >
                      {" "}
                      <div
                        style={{
                          height: "1px",
                          width: "100%",
                          background: "#eee",
                        }}
                      ></div>
                    </div>
                  )}
                  <div className={styles.blogBox}>
                    <div className={styles.imageBox}>
                      {" "}
                      <SafeImage
                        src={
                          isMobile
                            ? blog.mobilePreviewImageUrl ||
                              dynamicDefaultMobilePreview
                            : blog.desktopPreviewImageUrl ||
                              dynamicDefaultDesktopPreview
                        }
                        fallback={
                          isMobile
                            ? dynamicDefaultMobilePreview ||
                              mobileImageBackground
                            : dynamicDefaultDesktopPreview ||
                              desktopImageBackground
                        }
                        alt={blog.title}
                      />
                    </div>
                    <div className={styles.blogDetailsBox}>
                      <div>
                        <CustomText
                          size={isDesktop ? 20 : 14}
                          text={moment(blog.date).format("MMM DD, YYYY")}
                          color={theme.colors.neutral[20]}
                          styles={{
                            lineHeight: `${isDesktop ? 28 : 17}px`,
                            fontWeight: isDesktop ? 700 : 700,
                            marginBottom: isDesktop ? "10px" : "6px",
                          }}
                        />
                        <CustomHeadline
                          size={isDesktop ? 32 : 18}
                          text={
                            isDesktop
                              ? trimWithWordBoundary(blog.title, 120)
                              : trimWithWordBoundary(blog.title, 60)
                          }
                          color={theme.colors.neutral[20]}
                          styles={{
                            lineHeight: `${isDesktop ? 32 : 24}px`,
                            // fontWeight: 700,
                            marginBottom: "10px",
                          }}
                        />

                        <CustomText
                          size={isDesktop ? 28 : 14}
                          text={
                            isDesktop
                              ? trimWithWordBoundary(
                                  blog.description ?? "",
                                  250
                                )
                              : trimWithWordBoundary(
                                  blog.description ?? "",
                                  150
                                )
                          }
                          color={theme.colors.neutral[30]}
                          styles={{
                            lineHeight: `${isDesktop ? 40 : 17}px !important`,
                          }}
                        />
                      </div>
                      <CustomButtonSection
                        aria-label={`Read more about ${blog.title}`}
                        text="Read More"
                        colorScheme="green"
                        style={{
                          margin:
                            device === "tablet" || device === "desktop"
                              ? "30px 0px 0px"
                              : "20px auto 0px",
                          color: theme.colors.neutral[20],
                          border: "1px solid",
                          borderColor: "#00CB94",
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
